import { Copy, RotateCcw, ShieldAlert, ShieldCheck, Power, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { apiClient } from "@/lib/api";

const GROUP_LABELS = {
  scanner: "Scanner (1h)",
  signal_engine: "Signal Engine (15m)",
  bot_gate: "Bot Trader Gate",
};

const formatNumber = (val, kind) => {
  if (val === null || val === undefined) return "-";
  if (kind === "int") return Math.round(Number(val)).toString();
  const n = Number(val);
  if (!Number.isFinite(n)) return "-";
  // Trim trailing zeros but keep at least 1 decimal where relevant
  return n.toString();
};

export const AdminBotThresholdsPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [draftValues, setDraftValues] = useState({});
  const [applying, setApplying] = useState(false);
  const [restartCountdown, setRestartCountdown] = useState(0);

  const loadThresholds = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/admin/bot-thresholds");
      setData(res.data);
      // Reset draft to current runtime values
      const initial = {};
      (res.data?.thresholds || []).forEach((t) => {
        initial[t.env_key] = t.current;
      });
      setDraftValues(initial);
    } catch (err) {
      toast.error("Bot eşikleri alınamadı");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadThresholds();
  }, []);

  const groupedThresholds = useMemo(() => {
    const map = { scanner: [], signal_engine: [], bot_gate: [] };
    (data?.thresholds || []).forEach((t) => {
      if (!map[t.group]) map[t.group] = [];
      map[t.group].push(t);
    });
    return map;
  }, [data]);

  const envBlock = useMemo(() => {
    const lines = ["# === Bot Thresholds (P12) — backend/.env içine yapıştırın ==="];
    (data?.thresholds || []).forEach((t) => {
      const v = draftValues[t.env_key];
      const valStr = t.kind === "int" ? Math.round(Number(v)).toString() : Number(v).toString();
      lines.push(`${t.env_key}="${valStr}"`);
    });
    lines.push("# Değişiklikten sonra: sudo supervisorctl restart backend");
    return lines.join("\n");
  }, [data, draftValues]);

  const handleSliderChange = (envKey, value, kind) => {
    setDraftValues((prev) => ({
      ...prev,
      [envKey]: kind === "int" ? Math.round(Number(value)) : Number(value),
    }));
  };

  const resetToDefaults = () => {
    if (!data?.thresholds) return;
    const next = {};
    data.thresholds.forEach((t) => {
      next[t.env_key] = t.default;
    });
    setDraftValues(next);
    toast.success("Slider'lar default'a alındı (sadece preview, .env değişmedi)");
  };

  const resetToCurrent = () => {
    if (!data?.thresholds) return;
    const next = {};
    data.thresholds.forEach((t) => {
      next[t.env_key] = t.current;
    });
    setDraftValues(next);
    toast.success("Slider'lar runtime değerlerine alındı");
  };

  const copyEnvBlock = async () => {
    try {
      await navigator.clipboard.writeText(envBlock);
      toast.success("Clipboard'a kopyalandı. backend/.env içine yapıştırın.");
    } catch {
      toast.error("Clipboard erişimi reddedildi. Bloğu manuel kopyalayın.");
    }
  };

  const waitForBackendUp = async (maxAttempts = 30, delayMs = 2000) => {
    for (let i = 0; i < maxAttempts; i += 1) {
      setRestartCountdown(maxAttempts - i);
      try {
        // health endpoint mounted at /api/health
        const r = await apiClient.get("/health", { timeout: 1500 });
        if (r?.status === 200) {
          setRestartCountdown(0);
          return true;
        }
      } catch {
        // ignore; backend still restarting
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, delayMs));
    }
    setRestartCountdown(0);
    return false;
  };

  const applyAndRestart = async ({ enableLiveMode = false, restartAfter = true } = {}) => {
    if (!data?.thresholds) return;
    const updates = {};
    data.thresholds.forEach((t) => {
      const v = draftValues[t.env_key];
      if (v === undefined || v === null) return;
      updates[t.env_key] =
        t.kind === "int" ? Math.round(Number(v)).toString() : Number(v).toString();
    });
    const liveLabel = enableLiveMode ? " + LIVE MODE flag'leri" : "";
    const restartLabel = restartAfter
      ? "backend RESTART edilecek (~30sn kesinti)"
      : "RESTART YAPILMAYACAK (runtime cache otomatik yenilenir)";
    const confirmMsg =
      `${Object.keys(updates).length} eşik${liveLabel} backend/.env'e yazılacak ve ${restartLabel}. Devam edilsin mi?`;
    if (!window.confirm(confirmMsg)) return;
    setApplying(true);
    try {
      const res = await apiClient.post("/admin/bot-thresholds/apply", {
        updates,
        restart_after: restartAfter,
        enable_live_mode: enableLiveMode,
      });
      if (res?.data?.ok) {
        toast.success(
          `${res.data.applied_count} anahtar yazıldı. ${
            restartAfter ? "Backend yeniden başlatılıyor..." : "Runtime cache yenilendi."
          }`,
        );
        if (restartAfter) {
          // Wait briefly then poll health
          await new Promise((r) => setTimeout(r, 3500));
          const back = await waitForBackendUp();
          if (back) {
            toast.success("Backend ayağa kalktı. Yeni runtime değerleri yüklendi.");
            await loadThresholds();
          } else {
            toast.error("Backend 60sn'de geri gelmedi. Sunucudan logları kontrol edin.");
          }
        } else {
          // No restart — just refresh thresholds to show new runtime values
          await new Promise((r) => setTimeout(r, 600));
          await loadThresholds();
        }
      } else {
        toast.error("Apply isteği reddedildi.");
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "bilinmeyen hata";
      toast.error(`Apply başarısız: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return <LoadingSkeleton rows={8} testId="admin-bot-thresholds-loading" />;
  }

  if (!data) {
    return (
      <div className="p-6 text-slate-300" data-testid="admin-bot-thresholds-error">
        Veri alınamadı.
      </div>
    );
  }

  const summary = data.summary || {};

  return (
    <section className="grid grid-cols-12 gap-4" data-testid="admin-bot-thresholds-page">
      <header
        className="col-span-12 border border-slate-800 bg-slate-900 p-4"
        data-testid="admin-bot-thresholds-header"
      >
        <h2
          className="text-4xl font-black uppercase tracking-tight"
          data-testid="admin-bot-thresholds-title"
        >
          Bot Threshold Preview
        </h2>
        <p className="mt-2 text-sm text-slate-400" data-testid="admin-bot-thresholds-description">
          Read-only canlı runtime eşik değerleri. Slider'larla yeni değer seçin → "Copy .env block"
          ile clipboard'a kopyalayın → backend/.env'e yapıştırıp backend'i restart edin.
        </p>
      </header>

      {/* Summary banner */}
      <div
        className={`col-span-12 flex items-center gap-3 rounded border p-3 text-sm ${
          summary.is_live_mode
            ? "border-emerald-700 bg-emerald-950/30 text-emerald-100"
            : "border-amber-700 bg-amber-950/30 text-amber-100"
        }`}
        data-testid="admin-bot-thresholds-summary-banner"
      >
        {summary.is_live_mode ? (
          <ShieldCheck size={20} className="shrink-0" />
        ) : (
          <ShieldAlert size={20} className="shrink-0" />
        )}
        <div className="flex-1">
          <p className="font-medium" data-testid="admin-bot-thresholds-mode-label">
            {summary.is_live_mode ? "LIVE MODE — gerçek emir akışı aktif" : "Live mode kapalı — emir akışı yok"}
          </p>
          <p className="text-xs opacity-80">
            Diverging thresholds: {summary.diverging_thresholds ?? 0} ·{" "}
            Missing live flags: {summary.missing_live_flags_for_live ?? 0}
          </p>
        </div>
      </div>

      {/* Thresholds grouped */}
      {Object.entries(groupedThresholds).map(([groupKey, items]) => (
        <div
          key={groupKey}
          className="col-span-12 rounded border border-slate-800 bg-slate-950 p-4 lg:col-span-6"
          data-testid={`admin-bot-thresholds-group-${groupKey}`}
        >
          <h3
            className="mb-3 text-lg font-semibold uppercase tracking-wide text-slate-200"
            data-testid={`admin-bot-thresholds-group-${groupKey}-title`}
          >
            {GROUP_LABELS[groupKey] || groupKey}
          </h3>
          <div className="space-y-4">
            {items.map((t) => {
              const draftVal = draftValues[t.env_key] ?? t.current;
              const changed = Number(draftVal) !== Number(t.current);
              const isDefault = Number(draftVal) === Number(t.default);
              return (
                <div
                  key={t.env_key}
                  className="rounded border border-slate-800 bg-slate-900 p-3"
                  data-testid={`admin-bot-threshold-row-${t.env_key}`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <label
                      className="text-sm font-medium text-slate-200"
                      data-testid={`admin-bot-threshold-label-${t.env_key}`}
                    >
                      {t.label}
                    </label>
                    <span
                      className={`font-mono text-sm ${changed ? "text-amber-300" : "text-emerald-300"}`}
                      data-testid={`admin-bot-threshold-value-${t.env_key}`}
                    >
                      {formatNumber(draftVal, t.kind)}
                    </span>
                  </div>
                  <p
                    className="mt-1 text-xs text-slate-500"
                    data-testid={`admin-bot-threshold-desc-${t.env_key}`}
                  >
                    {t.description}
                  </p>
                  <input
                    type="range"
                    min={t.min}
                    max={t.max}
                    step={t.step}
                    value={draftVal}
                    onChange={(e) => handleSliderChange(t.env_key, e.target.value, t.kind)}
                    className="mt-2 w-full accent-orange-500"
                    data-testid={`admin-bot-threshold-slider-${t.env_key}`}
                  />
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span data-testid={`admin-bot-threshold-min-${t.env_key}`}>min: {t.min}</span>
                    <span data-testid={`admin-bot-threshold-max-${t.env_key}`}>max: {t.max}</span>
                    <span
                      className={isDefault ? "text-slate-400" : "text-amber-400"}
                      data-testid={`admin-bot-threshold-default-${t.env_key}`}
                    >
                      default: {formatNumber(t.default, t.kind)}
                    </span>
                    <span
                      className="text-slate-400"
                      data-testid={`admin-bot-threshold-current-${t.env_key}`}
                    >
                      runtime: {formatNumber(t.current, t.kind)}
                    </span>
                    <span
                      className="font-mono text-slate-600"
                      data-testid={`admin-bot-threshold-envkey-${t.env_key}`}
                    >
                      {t.env_key}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Live flags */}
      <div
        className="col-span-12 rounded border border-slate-800 bg-slate-950 p-4"
        data-testid="admin-bot-thresholds-live-flags-section"
      >
        <h3 className="mb-3 text-lg font-semibold uppercase tracking-wide text-slate-200">
          Live Mode Flags (.env runtime)
        </h3>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
          {(data.live_flags || []).map((f) => (
            <div
              key={f.env_key}
              className={`flex items-center justify-between rounded border px-3 py-2 text-xs ${
                f.ok
                  ? "border-emerald-800 bg-emerald-950/20 text-emerald-100"
                  : "border-amber-700 bg-amber-950/20 text-amber-100"
              }`}
              data-testid={`admin-bot-thresholds-live-flag-${f.env_key}`}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{f.label}</p>
                <p className="font-mono text-[10px] opacity-70">{f.env_key}</p>
              </div>
              <div className="ml-2 text-right">
                <p className="font-mono">{f.current ?? "-"}</p>
                <p className="text-[10px] opacity-60">expected: {f.expected_for_live}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action panel */}
      <div
        className="col-span-12 rounded border border-slate-800 bg-slate-950 p-4"
        data-testid="admin-bot-thresholds-action-panel"
      >
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => applyAndRestart({ enableLiveMode: false, restartAfter: false })}
            disabled={applying}
            className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
            data-testid="admin-bot-thresholds-apply-no-restart-btn"
            title="Restart YAPMADAN .env'e yazar ve runtime cache'i yeniden yükler. Bot bir sonraki iterasyonda yeni eşikleri kullanır."
          >
            <ShieldCheck size={16} className="mr-2" />
            Apply (Restart YOK)
          </Button>
          <Button
            type="button"
            onClick={() => applyAndRestart({ enableLiveMode: false, restartAfter: true })}
            disabled={applying}
            className="bg-red-600 text-white hover:bg-red-500 disabled:opacity-60"
            data-testid="admin-bot-thresholds-apply-restart-btn"
          >
            {applying ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                {restartCountdown > 0 ? `Backend bekleniyor (${restartCountdown})` : "Uygulanıyor..."}
              </>
            ) : (
              <>
                <Power size={16} className="mr-2" />
                Apply & Restart
              </>
            )}
          </Button>
          <Button
            type="button"
            onClick={() => applyAndRestart({ enableLiveMode: true, restartAfter: true })}
            disabled={applying}
            className="bg-amber-600 text-black hover:bg-amber-500 disabled:opacity-60"
            data-testid="admin-bot-thresholds-apply-live-btn"
            title="Eşikleri yazar + EXECUTION_MODE/CANARY_MODE/LIVE_SANITY_GATE_REQUIRED gibi 9 live flag'i de doğru değerlere set eder."
          >
            <ShieldCheck size={16} className="mr-2" />
            Apply + Live Mode'u Aç
          </Button>
          <Button
            type="button"
            onClick={copyEnvBlock}
            disabled={applying}
            variant="outline"
            data-testid="admin-bot-thresholds-copy-env-btn"
          >
            <Copy size={16} className="mr-2" />
            Copy .env block
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={resetToCurrent}
            disabled={applying}
            data-testid="admin-bot-thresholds-reset-current-btn"
          >
            <RotateCcw size={16} className="mr-2" />
            Slider'ları runtime'a sıfırla
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={resetToDefaults}
            disabled={applying}
            data-testid="admin-bot-thresholds-reset-defaults-btn"
          >
            Default'a al
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={loadThresholds}
            disabled={applying}
            data-testid="admin-bot-thresholds-refresh-btn"
          >
            Yenile
          </Button>
        </div>
        <p
          className="mt-2 text-xs text-amber-300"
          data-testid="admin-bot-thresholds-apply-warning"
        >
          ⚠ "Apply & Restart" backend/.env dosyasını sıfırdan yeniden yazar (mevcut anahtarlar korunur, sadece slider'da değişen anahtarlar üzerine yazılır) ve backend'i yeniden başlatır (~30sn kesinti, audit log'a kayıt). Üretimde dikkatli kullanın.
        </p>
        <pre
          className="mt-3 max-h-96 overflow-auto rounded border border-slate-800 bg-black p-3 text-xs text-emerald-200"
          data-testid="admin-bot-thresholds-env-block"
        >
{envBlock}
        </pre>
        <p
          className="mt-2 text-xs text-slate-500"
          data-testid="admin-bot-thresholds-guidance"
        >
          {data.guidance}
        </p>
      </div>
    </section>
  );
};

export default AdminBotThresholdsPage;
